/**
 * Vaultify – Rapid Search results width sync
 * ---------------------------------------------------------
 * Rapid Search (rapidsearch.app) renders its predictive-results panel
 * (#rapid-search-results) as a `position: fixed` element, portal-appended
 * outside the header (confirmed via its z-index: 2147483647, a value only
 * needed by an element escaping normal stacking context), centred on the
 * viewport via JS-computed inline `left: 50%; transform: translateX(-50%)`
 * and a JS-computed width unrelated to the search bar's own width.
 *
 * component-vaultify-rapid-search.css handles colour/shape/typography, but
 * width and position can't be matched with CSS alone here: the panel isn't
 * a CSS descendant of the search bar (so `position: absolute` relative to
 * it isn't available), and its inline width/position are recomputed by
 * Rapid Search's own script on every open — a static override would be
 * fought on the next render. This file measures the actual search bar
 * (.vaultify-header-search) and corrects the panel's inline style
 * immediately after Rapid Search repositions it, via a MutationObserver on
 * the panel's own `style` attribute (the only reliable "it just
 * repositioned itself" signal available, since Rapid Search dispatches no
 * public open/close event this theme can subscribe to).
 *
 * Deliberately does nothing on mobile (search bar hidden below 641px,
 * see base.css) — Rapid Search's own default centred/full-viewport
 * behaviour is reasonable there and this script has no anchor to sync
 * against.
 */
(function () {
  var SEARCH_BAR_SELECTOR = '.vaultify-header-search';
  var RESULTS_ID = 'rapid-search-results';

  function syncWidth() {
    var results = document.getElementById(RESULTS_ID);
    var bar = document.querySelector(SEARCH_BAR_SELECTOR);
    if (!results || !bar) return;
    if (getComputedStyle(results).display === 'none') return;

    var rect = bar.getBoundingClientRect();
    if (rect.width === 0) return; // search bar hidden (mobile) — leave Rapid Search's own default

    var targetWidth = Math.round(rect.width) + 'px';
    var targetLeft = Math.round(rect.left) + 'px';

    // Skip if already correct — required, not just an optimisation: this
    // function is called from a MutationObserver watching the very style
    // attribute it writes to, so writing unconditionally would trigger the
    // observer again on every call, forever.
    if (results.style.width === targetWidth && results.style.left === targetLeft) return;

    results.style.setProperty('width', targetWidth, 'important');
    results.style.setProperty('left', targetLeft, 'important');
    results.style.setProperty('right', 'auto', 'important');
    results.style.setProperty('transform', 'none', 'important');
  }

  var panelObserver = new MutationObserver(syncWidth);

  function watchPanel(el) {
    panelObserver.observe(el, { attributes: true, attributeFilter: ['style'] });
    syncWidth();
  }

  function init() {
    var existing = document.getElementById(RESULTS_ID);
    if (existing) {
      watchPanel(existing);
      return;
    }

    // Rapid Search creates #rapid-search-results lazily (on first search
    // interaction), so watch for its arrival, then switch to the narrower
    // style-attribute observer above and stop watching the whole body.
    var bodyObserver = new MutationObserver(function (_mutations, obs) {
      var el = document.getElementById(RESULTS_ID);
      if (el) {
        obs.disconnect();
        watchPanel(el);
      }
    });
    bodyObserver.observe(document.body, { childList: true, subtree: true });
  }

  window.addEventListener('resize', syncWidth);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();

/**
 * Vaultify – Mobile search: don't show native search if it's about to be
 * replaced
 * ---------------------------------------------------------
 * On mobile (<=640px), tapping the search icon opens Dawn's native
 * <details class="header__search"> modal instantly (plain HTML/browser
 * behaviour, no JS delay possible) — but Rapid Search's own
 * mobile-optimized overlay (a genuinely separate component, see
 * component-vaultify-rapid-search.css's header comment) takes a moment
 * to initialise and inject #rapid-search-results on top of it. In that
 * window Dawn's native search is fully interactive, so a customer who
 * starts typing into it loses whatever they typed the instant Rapid
 * Search's overlay covers the screen underneath them — the two are
 * entirely separate, unconnected inputs (confirmed live 2026-09-04).
 *
 * Fix: hide native search's interactive content (behind a lightweight
 * spinner, see the .search-modal--pending rule in base.css) the instant
 * the modal opens, and only reveal it if Rapid Search does NOT take
 * over within REVEAL_TIMEOUT_MS. This is deliberately not "always hide
 * native search" — Rapid Search is confirmed to not always initialise
 * on every real device (see CLAUDE.md Knowledge Log, 2026-09-04), and
 * native search is the only fallback those customers have. The timeout
 * guarantees it's never withheld indefinitely.
 */
(function () {
  var MOBILE_QUERY = '(max-width: 640px)';
  var REVEAL_TIMEOUT_MS = 1800;
  var RESULTS_ID = 'rapid-search-results';
  var PENDING_CLASS = 'search-modal--pending';
  var searchDetails = null;
  var pendingTimer = null;
  var pendingObserver = null;

  function isMobile() {
    return window.matchMedia(MOBILE_QUERY).matches;
  }

  function clearPending() {
    if (pendingTimer) {
      clearTimeout(pendingTimer);
      pendingTimer = null;
    }
    if (pendingObserver) {
      pendingObserver.disconnect();
      pendingObserver = null;
    }
  }

  function reveal() {
    clearPending();
    if (searchDetails) searchDetails.classList.remove(PENDING_CLASS);
  }

  function onToggle() {
    if (!searchDetails) return;
    clearPending();

    if (!searchDetails.open || !isMobile()) {
      searchDetails.classList.remove(PENDING_CLASS);
      return;
    }

    // Opened on mobile — hide native search's content until Rapid
    // Search's overlay appears (its z-index covers it anyway, so
    // nothing further to do then) or the timeout reveals it as a
    // fallback. The timeout always runs, even if #rapid-search-results
    // already exists from an earlier interaction this page — Rapid
    // Search re-displaying an existing element isn't observable the
    // same way its creation is, so the timeout is the only reliable
    // guarantee here too.
    searchDetails.classList.add(PENDING_CLASS);
    pendingTimer = setTimeout(reveal, REVEAL_TIMEOUT_MS);

    if (!document.getElementById(RESULTS_ID)) {
      pendingObserver = new MutationObserver(function () {
        if (document.getElementById(RESULTS_ID)) clearPending();
      });
      pendingObserver.observe(document.body, { childList: true, subtree: true });
    }
  }

  function init() {
    searchDetails = document.querySelector('details-modal.header__search > details');
    if (!searchDetails) return;
    searchDetails.addEventListener('toggle', onToggle);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
